import { skipToken } from '@reduxjs/toolkit/query';
import {
    ChevronDownIcon,
    CircleCheckBigIcon,
    FileTextIcon,
    MessageCircleIcon,
    type SvgIconComponent,
    TargetIcon,
    UsersIcon,
} from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { FOCUS_RING_INSET } from 'layout/NewDesignLayout/focusRing';
import { HoursFact, isMissingValue, MoneyFact } from 'pages/meeting-prep/InfoRow';
import { resolveMeetingPrepInternalHref } from 'pages/meeting-prep/meetingPrepLinks';
import { MeetingPrepMissingValue } from 'pages/meeting-prep/MeetingPrepMissingValue';
import { type ReactNode, useId, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { useGetMeetingPrepViewQuery } from 'store/api/aiChatApi';
import type { AiToolCallHistoryItem, PortalChatMessage } from 'types/aiChat';
import type { FigureDefinition, FigureRowsColumn } from 'types/aiChatFigure';
import type { MemberRow, MeetingPrepViewResponse } from 'types/meetingPrep';
import { cn } from 'utils/cn';
import { familyTileClass } from 'utils/families';
import { resolveRecordFromPath } from 'utils/globalSearchRoutes';
import { dateStringToDateObject } from 'utils/parseDate';
import { extractToolCallHistory } from '../../../utils/toolCallParts';
import MeetingPrepDownloadButton from '../../MeetingPrepDownloadButton';
import { MeetingPrepViewButton } from '../../MeetingPrepViewButton';
import { FigureCellValue } from './FigureCellValue';
import { FigureFrame } from './FigureFrame';
import { FigureReveal, FigureStatus } from './RowsFigure';

const BRIEF: FigureDefinition = { kind: 'brief' };

const RECORD_COLUMN: FigureRowsColumn = { field: 'name', label: '', type: 'record' };

// A section's body starts under its title, past the icon.
export const SECTION_BODY_CLASS_NAME = 'ps-[42px] pe-4';

const EMPTY_CLASS_NAME = cn('text-meta text-foreground-tertiary m-0 pb-3', SECTION_BODY_CLASS_NAME);

/**
 * The header's buttons sit at the end of the title row when the Figure is wide, and on their own row from the card's
 * left edge, in line with the tile, when it is narrow: the header row wraps, its actions take the whole row there,
 * and a title narrower than 200px sends them under it at any width.
 */
export const HEADER_WRAP_CLASS_NAME =
    '[&>div:first-child]:flex-wrap [&>div:first-child>div:nth-child(2)]:min-w-[200px] [&>div:first-child>div:last-child]:w-full @xl:[&>div:first-child>div:last-child]:w-auto';

/** The summaries the answer wrote, as the stream or the history hands them over: they fill the brief before it loads. */
const summariesOf = (toolCalls: AiToolCallHistoryItem[]) => {
    const output = toolCalls.findLast(
        ({ name, status }) => name === 'storeMeetingPrepSummaries' && status === 'success'
    )?.output;
    const summaries: Record<string, unknown> =
        typeof output === 'object' && output !== null ? (output as Record<string, unknown>) : {};
    const { suggestedDiscussionPoints: points, bdEffortsSummary } = summaries;
    return {
        points:
            Array.isArray(points) && points.every((point): point is string => typeof point === 'string')
                ? points
                : undefined,
        bdEffortsSummary: typeof bdEffortsSummary === 'string' ? bdEffortsSummary : undefined,
    };
};

/** The day the answer was written: its date in UTC, the zone the backend dates the brief in, as a local day to format. */
export const briefDay = (createdAt: string | undefined): Date | null => {
    const written = createdAt ? new Date(createdAt) : null;
    if (!written || Number.isNaN(written.getTime())) {
        return null;
    }
    return new Date(written.getUTCFullYear(), written.getUTCMonth(), written.getUTCDate());
};

export interface KeyNumber {
    label: string;
    value: ReactNode;
}

/** The brief page's own key facts, labelled and formatted as View brief shows them. */
const keyNumbersOf = (intl: IntlShape, view: MeetingPrepViewResponse): KeyNumber[] => {
    const label = (id: string) => intl.formatMessage({ id }, { yearsBack: String(view.brief.yearsBack) });
    if (view.type === 'GROUP') {
        const { brief } = view;
        return [
            {
                label: label('ai-chat.meeting-prep.members-covered'),
                value: `${brief.membersCoveredCount} / ${brief.groupTotalMemberCount}`,
            },
            {
                label: label('ai-chat.meeting-prep.group-info.billable-hours'),
                value: <HoursFact value={brief.billableHours3YValue} />,
            },
            {
                label: label('ai-chat.meeting-prep.group-info.total-hours'),
                value: <HoursFact value={brief.totalHours3YValue} />,
            },
        ];
    }
    const { brief } = view;
    const currency = brief.revenue3YValue !== null && brief.baseCurrencyCode ? brief.baseCurrencyCode : null;
    return [
        {
            label: label('ai-chat.meeting-prep.info.client-since'),
            value: isMissingValue(brief.clientSince) ? (
                <MeetingPrepMissingValue />
            ) : (
                <FormattedDate value={dateStringToDateObject(brief.clientSince)} fallback={brief.clientSince} />
            ),
        },
        { label: label('ai-chat.meeting-prep.info.case-count'), value: `${brief.caseCount3Y} / ${brief.caseCount1Y}` },
        {
            label: label('ai-chat.meeting-prep.info.billable-hours'),
            value: <HoursFact value={brief.billableHours3YValue} />,
        },
        {
            label: label('ai-chat.meeting-prep.info.revenue-3y'),
            value: (
                <>
                    <MoneyFact value={brief.revenue3YValue} />
                    {currency ? <span className="text-foreground-tertiary font-normal"> {currency}</span> : null}
                </>
            ),
        },
    ];
};

// Four key numbers pair up in the panel and sit in one row on the page; three share a row everywhere.
const keyNumberBasis = (count: number) => (count > 3 ? 'flex-[1_1_8rem]' : 'flex-[1_1_7rem]');

/** The row the key numbers and their bars lay out in. */
export const KEY_NUMBERS_CLASS_NAME = 'flex flex-wrap items-end gap-x-4 gap-y-3 px-4 pb-3';

/** A Figure's key numbers, each a label over its value. */
export function KeyNumbers({ numbers }: { numbers: KeyNumber[] }) {
    return (
        <dl className={cn('m-0', KEY_NUMBERS_CLASS_NAME)}>
            {numbers.map(({ label, value }) => (
                <div key={label} className={cn('flex flex-col gap-1', keyNumberBasis(numbers.length))}>
                    <dt className="text-caption text-foreground-tertiary">{label}</dt>
                    <dd className="text-section text-foreground m-0 tabular-nums">{value}</dd>
                </div>
            ))}
        </dl>
    );
}

export function KeyNumbersSkeleton({ count }: { count: number }) {
    return Array.from({ length: count }, (_, index) => (
        <div key={index} className={cn('flex flex-col gap-1.5', keyNumberBasis(count))}>
            <div className="bg-muted h-3 w-20 rounded-sm" />
            <div className="bg-muted h-5 w-16 rounded-sm" />
        </div>
    ));
}

interface SectionProps {
    icon: SvgIconComponent;
    title: string;
    count?: number;
    defaultOpen?: boolean;
    children: ReactNode;
}

/** One of the brief's sections, an accordion: a closed one leaves the Tab order and the accessibility tree. */
export function BriefSection({ icon: Icon, title, count, defaultOpen = false, children }: SectionProps) {
    const [open, setOpen] = useState(defaultOpen);
    const panelId = useId();
    return (
        <div className="t-acc border-divider-soft border-t" data-open={open ? 'true' : 'false'}>
            <button
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen(!open)}
                className={cn(
                    'hover:bg-row-stripe flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-start',
                    FOCUS_RING_INSET
                )}
            >
                <Icon className="text-foreground-tertiary size-4 shrink-0" />
                <span className="text-foreground min-w-0 flex-1 font-medium">{title}</span>
                {count === undefined ? null : (
                    <span className="bg-neutral-tint text-caption text-foreground-secondary rounded-lg px-1.5 font-medium tabular-nums">
                        {count}
                    </span>
                )}
                <span className="t-acc-chevron text-foreground-tertiary">
                    <ChevronDownIcon className="size-4" />
                </span>
            </button>
            <div id={panelId} className="t-acc-panel">
                <div className="t-acc-panel-inner">{children}</div>
            </div>
        </div>
    );
}

/** A section's body: bars until its content lands, then the content, in the same slot. */
export function SectionReveal({ loading, children }: { loading: boolean; children: ReactNode }) {
    return (
        <FigureReveal loading={loading} skeletonClassName={cn('flex flex-col gap-2 pb-3', SECTION_BODY_CLASS_NAME)}>
            {children}
        </FigureReveal>
    );
}

/** A group member: a contact chip from the record its link names, or the name alone where it names none. */
function MemberName({ member }: { member: MemberRow }) {
    const path = member.url ? resolveMeetingPrepInternalHref(member.url) : null;
    const record = path ? resolveRecordFromPath(path) : null;
    return record ? (
        <FigureCellValue cell={{ ...record, label: member.name }} column={RECORD_COLUMN} />
    ) : (
        <span className="text-foreground">{member.name}</span>
    );
}

function MembersTable({ members }: { members: MemberRow[] }) {
    const intl = useIntl();
    const count = (value: number | null) => (value === null ? <MeetingPrepMissingValue /> : value);
    if (members.length === 0) {
        return (
            <p className={EMPTY_CLASS_NAME}>{intl.formatMessage({ id: 'ai-chat.meeting-prep.group-members.empty' })}</p>
        );
    }
    return (
        <div className={cn('pb-3', SECTION_BODY_CLASS_NAME)}>
            <table className="text-meta w-full border-collapse">
                <thead>
                    <tr className="text-caption text-foreground-tertiary">
                        <th scope="col" className="py-1 pe-2 text-start font-medium">
                            {intl.formatMessage({ id: 'ai-chat.meeting-prep.group-members.table.name' })}
                        </th>
                        <th scope="col" className="px-2 py-1 text-end font-medium">
                            {intl.formatMessage({ id: 'ai-chat.meeting-prep.group-members.table.efforts' })}
                        </th>
                        <th scope="col" className="py-1 ps-2 text-end font-medium">
                            {intl.formatMessage({ id: 'ai-chat.meeting-prep.group-members.table.cases' })}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {members.map((member, index) => (
                        <tr key={`${member.name}-${index}`} className="border-divider-soft border-t">
                            {/* The name takes what the counts leave, so a long one truncates in the card. */}
                            <td className="w-full max-w-0 py-1.5 pe-2">
                                <MemberName member={member} />
                            </td>
                            <td className="text-foreground-secondary px-2 py-1.5 text-end whitespace-nowrap tabular-nums">
                                {count(member.bdEffortCount)}
                            </td>
                            <td className="text-foreground-secondary py-1.5 ps-2 text-end whitespace-nowrap tabular-nums">
                                {count(member.caseCount)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

interface Props {
    /** The answer that prepared the brief: its stored brief, the day it was written, and its streamed summaries. */
    message: PortalChatMessage;
}

/**
 * A meeting brief Figure, a client's or a group's as the stored brief's own type says, laid out as the brief page
 * lays it out, with the page's labels: the key numbers, then Suggested Discussion Points, Group Members for a group,
 * BD Efforts and Incomplete BD Tasks. The rest of the brief stays behind View brief. Until the answer's id arrives
 * and while the brief loads, its parts pulse, its meta line says the sections fill in and its buttons wait unseen; the
 * discussion points and the BD efforts summary fill in from the summaries the answer streams. A reopened chat shows
 * the brief as it was written, never prepared again.
 */
export function BriefFigure({ message }: Props) {
    const intl = useIntl();
    const aiChatMessageId = message.metadata?.aiChatMessageId;
    const id = typeof aiChatMessageId === 'number' ? aiChatMessageId : undefined;
    const query = useGetMeetingPrepViewQuery(id ?? skipToken);
    const view = query.data;
    const toolCalls = extractToolCallHistory(message.parts);
    const summaries = summariesOf(toolCalls);
    // The stored brief's type, known before it loads from the tool that prepared it.
    const group = view ? view.type === 'GROUP' : toolCalls.some(({ name }) => name === 'generateGroupMeetingPrep');
    const loading = !view && !query.isError;

    const day = briefDay(message.metadata?.createdAt);
    const meta = loading ? (
        intl.formatMessage({ id: 'ai-chat.meeting-prep.filling-in' })
    ) : (
        <>
            <FormattedMessage
                id="ai-chat.meeting-prep.generated"
                values={{ date: day ? <FormattedDate value={day} /> : (view?.generatedDate ?? '') }}
            />
            {view
                ? ` · ${intl.formatMessage(
                      { id: 'ai-chat.meeting-prep.group-info.time-window-value' },
                      { yearsBackLabel: view.brief.yearsBackLabel }
                  )}`
                : null}
        </>
    );
    const title = view
        ? intl.formatMessage(
              { id: 'ai-chat.meeting-prep.title' },
              { clientName: view.type === 'GROUP' ? view.brief.groupName : view.brief.clientName }
          )
        : intl.formatMessage({ id: 'ai-chat.meeting-prep.title-pending' });

    const keyNumbers = view ? keyNumbersOf(intl, view) : [];
    const points = view?.brief.suggestedDiscussionPoints ?? summaries.points;
    const bdEffortsSummary = view?.brief.bdEffortsSummary ?? summaries.bdEffortsSummary;

    let body: ReactNode;
    if (query.isError) {
        body = <FigureStatus definition={BRIEF} empty={false} query={query} />;
    } else {
        body = (
            <>
                <FigureReveal
                    loading={loading}
                    skeleton={<KeyNumbersSkeleton count={group ? 3 : 4} />}
                    skeletonClassName={KEY_NUMBERS_CLASS_NAME}
                >
                    <KeyNumbers numbers={keyNumbers} />
                </FigureReveal>
                <BriefSection
                    icon={MessageCircleIcon}
                    title={intl.formatMessage({ id: 'ai-chat.meeting-prep.discussion-points.title' })}
                    count={points?.length}
                    defaultOpen
                >
                    <SectionReveal loading={!points}>
                        {points && points.length > 0 ? (
                            <ol
                                className={cn(
                                    'text-foreground m-0 flex list-none flex-col gap-1.5 pb-3',
                                    SECTION_BODY_CLASS_NAME
                                )}
                            >
                                {points.map((point, index) => (
                                    <li key={`${index}-${point.slice(0, 20)}`} className="flex gap-2.5">
                                        <span className="text-meta text-foreground-tertiary w-2 shrink-0 leading-[1.5715] tabular-nums">
                                            {index + 1}
                                        </span>
                                        <span className="text-pretty">{point}</span>
                                    </li>
                                ))}
                            </ol>
                        ) : null}
                        {points?.length === 0 && !view?.brief.analysisUnavailable ? (
                            <p className={EMPTY_CLASS_NAME}>
                                {intl.formatMessage({ id: 'ai-chat.meeting-prep.discussion-points.empty' })}
                            </p>
                        ) : null}
                    </SectionReveal>
                </BriefSection>
                {group ? (
                    <BriefSection
                        icon={UsersIcon}
                        title={intl.formatMessage({ id: 'ai-chat.meeting-prep.group-members.title' })}
                        count={view?.type === 'GROUP' ? view.brief.members.length : undefined}
                    >
                        <SectionReveal loading={!view}>
                            {view?.type === 'GROUP' ? <MembersTable members={view.brief.members} /> : null}
                        </SectionReveal>
                    </BriefSection>
                ) : null}
                <BriefSection
                    icon={TargetIcon}
                    title={intl.formatMessage({ id: 'ai-chat.meeting-prep.bd-efforts.title' })}
                    count={view?.brief.bdEffortCount}
                >
                    <SectionReveal loading={!view && bdEffortsSummary === undefined}>
                        {bdEffortsSummary ? (
                            <p className={cn('text-foreground m-0 pb-3.5 text-pretty', SECTION_BODY_CLASS_NAME)}>
                                {bdEffortsSummary}
                            </p>
                        ) : null}
                        {!bdEffortsSummary && view?.brief.bdEffortCount === 0 ? (
                            <p className={EMPTY_CLASS_NAME}>
                                {intl.formatMessage(
                                    { id: 'ai-chat.meeting-prep.bd-efforts.empty' },
                                    { yearsBackLabel: view.brief.yearsBackLabel }
                                )}
                            </p>
                        ) : null}
                    </SectionReveal>
                </BriefSection>
                <BriefSection
                    icon={CircleCheckBigIcon}
                    title={intl.formatMessage({ id: 'ai-chat.meeting-prep.incomplete-bd-tasks.title' })}
                    count={view?.brief.totalBdTaskCount}
                >
                    <SectionReveal loading={!view}>
                        {view && view.brief.incompleteBdTasks.length > 0 ? (
                            <ul className={cn('m-0 flex list-none flex-col gap-1.5 pb-3.5', SECTION_BODY_CLASS_NAME)}>
                                {view.brief.incompleteBdTasks.map((task, index) => (
                                    <li
                                        key={`${task.name}-${index}`}
                                        className="flex flex-wrap items-baseline justify-between gap-x-3"
                                    >
                                        <span className="text-foreground">{task.name}</span>
                                        <span className="text-caption text-foreground-tertiary tabular-nums">
                                            {task.createdDate}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                        {view?.brief.incompleteBdTasks.length === 0 ? (
                            <p className={EMPTY_CLASS_NAME}>
                                {intl.formatMessage({
                                    id: group
                                        ? 'ai-chat.meeting-prep.incomplete-bd-tasks.empty-group'
                                        : 'ai-chat.meeting-prep.incomplete-bd-tasks.empty',
                                })}
                            </p>
                        ) : null}
                    </SectionReveal>
                </BriefSection>
            </>
        );
    }

    return (
        <FigureFrame
            icon={FileTextIcon}
            tileClassName={familyTileClass('bd')}
            title={title}
            meta={meta}
            busy={loading}
            className={cn('my-3', HEADER_WRAP_CLASS_NAME)}
            actions={
                loading ? undefined : (
                    <div className="flex flex-wrap items-center gap-2">
                        <MeetingPrepViewButton aiChatMessageId={id} />
                        <MeetingPrepDownloadButton aiChatMessageId={id} />
                    </div>
                )
            }
        >
            {body}
        </FigureFrame>
    );
}
